import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, Calendar, Users } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export default function Tournaments() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = filter === "all" ? {} : { status: filter };
    setLoading(true);
    api
      .get("/tournaments", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [filter]);

  const filters = [
    { k: "all", l: "Alle" },
    { k: "upcoming", l: "Kommend" },
    { k: "live", l: "Live" },
    { k: "completed", l: "Beendet" },
  ];

  return (
    <div className="space-y-8 fade-in" data-testid="tournaments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">Wettkämpfe</div>
          <h1 className="font-display text-4xl tracking-tight mt-2">Alle Turniere</h1>
          <p className="text-white/60 text-sm mt-2">Durchsuche alle laufenden und kommenden Events.</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] font-mono border transition-colors ${
              filter === f.k
                ? "bg-primary/10 border-primary text-primary"
                : "border-border text-white/60 hover:border-primary/40 hover:text-white"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-mono text-xs text-white/40 uppercase tracking-[0.2em]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs text-white/40 uppercase tracking-[0.2em]">
          Keine Turniere in dieser Kategorie.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((t) => (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              data-testid={`tournament-card-${t.id}`}
              className="card-solid overflow-hidden hover:border-primary/50 transition-colors group"
            >
              {t.image_url && (
                <div className="relative h-40 overflow-hidden">
                  <img src={t.image_url} alt={t.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                  <div className="absolute top-3 left-3 chip backdrop-blur-md bg-black/40">
                    {t.status === "live" && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                    {t.status}
                  </div>
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 text-primary">
                  <Trophy className="w-3.5 h-3.5" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{t.game}</span>
                </div>
                <div className="font-display text-xl mt-3 tracking-tight">{t.name}</div>
                <div className="text-xs text-white/50 font-mono mt-1">{t.format}</div>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 text-white/60 font-mono">
                    <Calendar className="w-3 h-3" /> {formatDate(t.start_time)} · {formatTime(t.start_time)}
                  </div>
                  <div className="flex items-center gap-1.5 text-white/60 font-mono">
                    <Users className="w-3 h-3" /> {t.participants?.length || 0}/{t.max_players}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
